import {
  Alert,
  Button,
  Card,
  Group,
  NumberInput,
  SegmentedControl,
  Stack,
  Switch,
  TagsInput,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { usePermission } from "@how/app-kit";
import { ErrorState, LoadingState, PageHeader } from "@how/ui";
import { moneyValue } from "../../lib/money-input";
import { StoreTabs } from "./StoreTabs";
import {
  type StoreMode,
  type StoreSettings,
  useSaveStoreSettings,
  useStoreSettings,
} from "./store-queries";

const SHOPIFY_URL = /^https:\/\/[^\s/?#]+\.[^\s/?#]+(\/\S*)?$/;

const dollars = (cents: number | null): number | "" => (cents === null ? "" : cents / 100);
const cents = (value: number | ""): number | null =>
  value === "" ? null : Math.round(value * 100);

/**
 * Store → Settings (ADR 0047 step 2): how members receive what they buy (picked up at church, shipped, or both), the
 * church's flat shipping rate, and whether Stripe Tax collects sales tax (off unless the church switches it on).
 */
export function StoreSettingsPage() {
  const settings = useStoreSettings();
  return (
    <>
      <PageHeader
        title="Store"
        description="How members receive what they buy, what shipping costs, and sales tax."
      />
      <StoreTabs active="settings" />
      {settings.isPending ? (
        <LoadingState rows={4} />
      ) : settings.isError ? (
        <ErrorState message={settings.error.message} onRetry={() => void settings.refetch()} />
      ) : (
        <SettingsForm saved={settings.data} />
      )}
    </>
  );
}

function SettingsForm({ saved }: { readonly saved: StoreSettings }) {
  const canManage = usePermission("commerce.manage_catalog", { module: "commerce", write: true });
  const save = useSaveStoreSettings();
  const [mode, setMode] = useState<StoreMode>(saved.mode);
  const [shopifyUrl, setShopifyUrl] = useState(saved.shopifyUrl);
  const [pickup, setPickup] = useState(saved.pickupEnabled);
  const [pickupNote, setPickupNote] = useState(saved.pickupNote);
  const [shipping, setShipping] = useState(saved.shippingEnabled);
  const [rate, setRate] = useState<number | "">(dollars(saved.shippingRateCents));
  const [freeOver, setFreeOver] = useState<number | "">(dollars(saved.freeShippingOverCents));
  const [countries, setCountries] = useState<string[]>([...saved.shipCountries]);
  const [tax, setTax] = useState(saved.taxEnabled);

  const readOnly = canManage.data !== true;
  const shopifyValid = mode !== "shopify" || SHOPIFY_URL.test(shopifyUrl.trim());
  const valid =
    shopifyValid && (pickup || shipping) && (!shipping || (rate !== "" && countries.length > 0));

  return (
    <Stack gap="lg" maw={720}>
      {readOnly ? (
        <Alert variant="light" color="gray">
          Only people who manage the store's products can change these settings.
        </Alert>
      ) : null}
      <Card withBorder padding="lg">
        <Stack gap="sm">
          <Title order={3} size="h5">
            Where members shop
          </Title>
          <SegmentedControl
            aria-label="Where members shop"
            value={mode}
            disabled={readOnly}
            onChange={(value) => {
              setMode(value as StoreMode);
            }}
            data={[
              { value: "how", label: "HoW's store" },
              { value: "shopify", label: "Our Shopify store" },
              { value: "none", label: "No store" },
            ]}
          />
          <Text size="sm" c="dimmed">
            {mode === "how"
              ? "Members buy in the portal's Shop, paid into the church's Stripe account."
              : mode === "shopify"
                ? "The portal's Shop sends members to your Shopify store; HoW sells nothing there. Event add-ons stay in HoW."
                : "The Shop leaves the portal. Event add-ons stay in HoW."}{" "}
            Orders already placed are not affected.
          </Text>
          {mode === "shopify" ? (
            <TextInput
              label="Your Shopify store's address"
              placeholder="https://merch.yourchurch.org"
              maxLength={300}
              disabled={readOnly}
              value={shopifyUrl}
              error={
                shopifyUrl.trim() !== "" && !shopifyValid
                  ? "Use the full address, starting with https://"
                  : undefined
              }
              onChange={(event) => {
                setShopifyUrl(event.currentTarget.value);
              }}
            />
          ) : null}
        </Stack>
      </Card>
      {mode === "how" ? null : (
        <Text size="sm" c="dimmed">
          The settings below apply to HoW's store only; they are kept for when you switch back.
        </Text>
      )}
      <Card withBorder padding="lg">
        <Stack gap="sm">
          <Title order={3} size="h5">
            Pickup at church
          </Title>
          <Switch
            label="Members can pick up what they buy at church"
            checked={pickup}
            disabled={readOnly}
            onChange={(event) => {
              setPickup(event.currentTarget.checked);
            }}
          />
          <TextInput
            label="Where and when"
            description="Shown in the Shop and on the member's order."
            placeholder="At the welcome desk after any service"
            maxLength={200}
            disabled={readOnly || !pickup}
            value={pickupNote}
            onChange={(event) => {
              setPickupNote(event.currentTarget.value);
            }}
          />
        </Stack>
      </Card>
      <Card withBorder padding="lg">
        <Stack gap="sm">
          <Title order={3} size="h5">
            Shipping
          </Title>
          <Switch
            label="Ship orders to members"
            description="Stripe's checkout asks for the address; you mark each order shipped with its tracking number."
            checked={shipping}
            disabled={readOnly}
            onChange={(event) => {
              setShipping(event.currentTarget.checked);
            }}
          />
          <Group grow align="flex-start">
            <NumberInput
              label="Shipping per order"
              description="One flat rate, whatever the order holds."
              prefix="$"
              decimalScale={2}
              fixedDecimalScale
              min={0}
              max={1000}
              disabled={readOnly || !shipping}
              value={rate}
              onChange={(value) => {
                setRate(moneyValue(value));
              }}
            />
            <NumberInput
              label="Free shipping over"
              description="Leave empty if shipping is never free."
              prefix="$"
              decimalScale={2}
              fixedDecimalScale
              min={0}
              disabled={readOnly || !shipping}
              value={freeOver}
              onChange={(value) => {
                setFreeOver(moneyValue(value));
              }}
            />
          </Group>
          <TagsInput
            label="Countries you ship to"
            description="Two-letter codes, such as US or CA. Press Enter after each."
            disabled={readOnly || !shipping}
            value={countries}
            onChange={(value) => {
              setCountries(value.map((c) => c.trim().toUpperCase()));
            }}
          />
        </Stack>
      </Card>
      <Card withBorder padding="lg">
        <Stack gap="sm">
          <Title order={3} size="h5">
            Sales tax
          </Title>
          <Switch
            label="Collect sales tax at checkout with Stripe Tax"
            checked={tax}
            disabled={readOnly}
            onChange={(event) => {
              setTax(event.currentTarget.checked);
            }}
          />
          <Text size="sm" c="dimmed">
            Off unless you switch it on. Stripe calculates the tax from the buyer's address and
            charges a fee for it on the church's Stripe account. Set up Stripe Tax in the church's
            Stripe dashboard first, with the church's address and the states it collects in; until
            then, checkout tells members the store cannot take payment. Whether the church must
            collect sales tax depends on its state and what it sells: ask the church's accountant.
          </Text>
        </Stack>
      </Card>
      {!pickup && !shipping ? (
        <Alert variant="light" color="orange">
          Members need a way to get what they buy: turn on pickup, shipping, or both.
        </Alert>
      ) : null}
      {save.error ? (
        <Alert variant="light" color="red">
          {save.error.message}
        </Alert>
      ) : null}
      {readOnly ? null : (
        <Group>
          <Button
            loading={save.isPending}
            disabled={!valid}
            onClick={() => {
              save.mutate(
                {
                  mode,
                  shopifyUrl,
                  pickupEnabled: pickup,
                  pickupNote,
                  shippingEnabled: shipping,
                  shippingRateCents: cents(rate) ?? 0,
                  freeShippingOverCents: cents(freeOver),
                  shipCountries: countries,
                  taxEnabled: tax,
                },
                {
                  onSuccess: () => {
                    notifications.show({ message: "Store settings saved" });
                  },
                },
              );
            }}
          >
            Save settings
          </Button>
        </Group>
      )}
    </Stack>
  );
}
